import React from 'react';
import StatisticsBox from '../components/StatisticsBox';
import ReadinessGauge from '../components/ReadinessGauge';
import DistributionChart from '../components/DistributionChart';
import PerformanceChart from '../components/PerformanceChart';
import styles from './DirectorView.module.css';
import layoutStyles from '../WindowReadinessResults.module.css';

const DirectorView = ({ data }) => {
  if (!data) return <div className="loading">Loading Director view...</div>;

  const { directorStats, wowComparison, mumPerformance, locations, director, isFirstUpload } = data;

  // Prepare MUM performance data for chart
  const mumChartData = mumPerformance.map((mum) => ({
    label: mum.mum,
    value: parseFloat(mum.completion_percentage),
  }));

  // Get top 10 locations
  const topLocations = locations.slice(0, 10);
  const topLocationsChartData = topLocations.map((location) => ({
    label: `${location.training_location_name}`,
    value: parseFloat(location.completion_percentage),
  }));

  // Determine action items
  const getActionItems = () => {
    const items = [];
    const completion = parseFloat(directorStats.overall_completion);

    if (isFirstUpload) {
      if (completion >= 80) {
        items.push({ type: 'success', text: 'Team is compliant - maintain current performance' });
      } else if (completion >= 60) {
        items.push({ type: 'warning', text: 'Focus coaching efforts on locations below 80%' });
        items.push({ type: 'info', text: `${directorStats.locations_critical} locations need immediate support` });
      } else {
        items.push({ type: 'critical', text: 'Schedule urgent meetings with MUMs for locations below 60%' });
        items.push({ type: 'critical', text: 'Deploy additional training resources' });
      }
    } else {
      if (wowComparison.trend === 'up') {
        items.push({ type: 'success', text: `Team improved by ${wowComparison.change.toFixed(1)}%` });
        items.push({ type: 'info', text: 'Share success strategies with other directors' });
      } else if (wowComparison.trend === 'down') {
        items.push({ type: 'warning', text: `Team completion declined by ${Math.abs(wowComparison.change).toFixed(1)}%` });
        items.push({ type: 'warning', text: 'Review barriers with MUMs and provide support' });
      }

      if (directorStats.locations_critical > 0) {
        items.push({ type: 'critical', text: `${directorStats.locations_critical} locations require immediate intervention` });
      }
    }

    return items;
  };

  const actionItems = getActionItems();

  return (
    <div className={styles.directorView}>
      <div className={styles.viewHeader}>
        <h2>Director: {director}</h2>
      </div>

      <StatisticsBox stats={directorStats} wowComparison={wowComparison} />

      <div className={layoutStyles.chartsGrid}>
        <ReadinessGauge
          percentage={parseFloat(directorStats.overall_completion)}
          title="Team Readiness"
        />
        <DistributionChart
          ready={directorStats.locations_ready}
          caution={directorStats.locations_caution}
          critical={directorStats.locations_critical}
          title="Location Distribution"
        />
      </div>

      <div className={layoutStyles.chartsGrid}>
        <PerformanceChart
          data={mumChartData}
          title="MUM Performance"
        />
        <PerformanceChart
          data={topLocationsChartData}
          title="Top 10 Locations Needing Attention"
        />
      </div>

      <div className={layoutStyles.actionItems}>
        <h3>Action Items</h3>
        <ul>
          {actionItems.map((item, index) => (
            <li key={index} className={layoutStyles[`actionItem${item.type.charAt(0).toUpperCase() + item.type.slice(1)}`]}>
              {item.text}
            </li>
          ))}
        </ul>
      </div>

      <div className={layoutStyles.dataTable}>
        <h3>MUM Summary</h3>
        <div className={layoutStyles.tableResponsive}>
          <table>
            <thead>
              <tr>
                <th>MUM</th>
                <th>Locations</th>
                <th>Total Employees</th>
                <th>Completed</th>
                <th>Completion %</th>
              </tr>
            </thead>
            <tbody>
              {mumPerformance.map((mum, index) => (
                <tr key={index} className={layoutStyles[`row${mum.completion_percentage >= 80 ? 'Ready' : mum.completion_percentage >= 60 ? 'Caution' : 'Critical'}`]}>
                  <td>{mum.mum}</td>
                  <td>{mum.location_count}</td>
                  <td>{mum.total_employees.toLocaleString()}</td>
                  <td>{mum.employees_completed.toLocaleString()}</td>
                  <td>
                    <span className={layoutStyles.completionBadge}>{mum.completion_percentage}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={layoutStyles.dataTable}>
        <h3>Location Details (Sorted by Priority)</h3>
        <div className={layoutStyles.tableResponsive}>
          <table>
            <thead>
              <tr>
                <th>MUM</th>
                <th>Location Name</th>
                <th>Status</th>
                <th>Employee Count</th>
                <th>Completed</th>
                <th>Completion %</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location, index) => (
                <tr key={index} className={layoutStyles[`row${location.completion_percentage >= 80 ? 'Ready' : location.completion_percentage >= 60 ? 'Caution' : 'Critical'}`]}>
                  <td>{location.mum}</td>
                  <td>{location.training_location_name}</td>
                  <td>
                    <span className={`${layoutStyles.statusBadge} ${layoutStyles[`status${location.status_color.charAt(0).toUpperCase() + location.status_color.slice(1)}`]}`}>
                      {location.compliance_status}
                    </span>
                  </td>
                  <td>{location.employee_count}</td>
                  <td>{location.employees_completed}</td>
                  <td>
                    <span className={layoutStyles.completionBadge}>{location.completion_percentage}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DirectorView;
